
import { supabase } from '@/integrations/supabase/client';
import { getCurrentDomain } from '@/utils/domainUtils';
import { getOrCreateVisitorId } from '@/utils/referralUtils';
import { PendingReferralResult } from '@/types/referral';

/**
 * Records a referral conversion for a given referral code
 * @param referralCode The referral code to record the conversion for
 * @param source Optional source of the conversion (for analytics)
 */
export async function recordReferralConversion(
  referralCode: string,
  _contentType: string = 'unknown',
  _contentId: string = '',
  source: string = 'content_share'
): Promise<void> {
  if (!referralCode) return;
  
  try {
    console.log("Recording referral conversion for code:", referralCode);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.warn("No authenticated user for referral conversion");
      return;
    }
    
    // Get the referrer from the referral code
    const { data: referralCodeData } = await supabase
      .from('referral_codes')
      .select('user_id')
      .eq('code', referralCode)
      .single();
    
    if (referralCodeData) {
      // Insert the referral conversion directly
      const { error } = await supabase
        .from('referrals')
        .insert({
          referrer_id: referralCodeData.user_id,
          referred_user_id: user.id,
          source: source,
          status: 'converted'
        });
      
      if (error) {
        console.error("Error inserting referral conversion:", error);
      }
    }
  } catch (error) {
    console.error("Error in recordReferralConversion:", error);
    // Non-critical operation, don't re-throw
  }
}

/**
 * Generates a shareable URL for content
 * @param shareId The unique share ID for the content
 * @param contentType The type of content being shared (game-plan or technique)
 * @param referralCode Optional referral code to track the share source
 * @returns A complete shareable URL
 */
export function generateShareableUrl(shareId: string, contentType: 'game-plan' | 'technique' = 'game-plan', referralCode?: string): string {
  if (!shareId) return '';
  
  // Always use the production domain for shareable URLs
  const baseUrl = getCurrentDomain(true);
  let url = `${baseUrl}/shared/${contentType}/${shareId}`;
  
  if (referralCode) {
    url += `?ref=${referralCode}`;
  }
  
  return url;
}

/**
 * Checks for pending referrals for the current user by email or visitor ID
 * @param email The user's email
 * @param subscriptionType The type of subscription (monthly or annual)
 * @returns Referral information if found
 */
export async function checkPendingReferral(
  email?: string,
  _subscriptionType: 'monthly' | 'annual' = 'monthly'
): Promise<PendingReferralResult> {
  try {
    // Get visitor ID from storage if available
    const visitorId = getOrCreateVisitorId();
    
    // If we have neither email nor visitor ID, return early
    if (!email && !visitorId) {
      return { found: false };
    }
    
    console.log(`Checking for pending referral with ${email ? 'email' : 'visitor ID'}`);
    
    // Query pending referrals directly with proper OR condition
    let query = supabase
      .from('pending_referrals')
      .select('*');

    // Build OR condition for email or visitor_id
    if (email && visitorId) {
      query = query.or(`email.eq.${email},visitor_id.eq.${visitorId}`);
    } else if (email) {
      query = query.eq('email', email);
    } else if (visitorId) {
      query = query.eq('visitor_id', visitorId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error("Error checking pending referral:", error);
      return { found: false };
    }

    if (!data) {
      return { found: false };
    }

    console.log("Pending referral check result:", data);
    return {
      found: true,
      referral_code: data.referral_code,
      promotion_amount: data.promotion_amount,
      promotion_description: data.promotion_description,
      visitor_id: data.visitor_id,
      referrer_id: data.referrer_id,
      campaign_source: data.campaign_source
    };
  } catch (error) {
    console.error("Error in checkPendingReferral:", error);
    return { found: false };
  }
}

/**
 * Generate app store links with proper UTM tracking parameters
 * @param referralCode Optional referral code to include
 */
export function generateAppStoreLinks(referralCode?: string) {
  // Your app's store URLs - replace with actual URLs when available
  const appleBaseUrl = "https://apps.apple.com/app/the-mat-monster/id1234567890";
  const googleBaseUrl = "https://play.google.com/store/apps/details?id=com.matmonster.app";
  
  // Get visitor ID for tracking
  const visitorId = getOrCreateVisitorId();
  
  // Build UTM parameters
  const utm = new URLSearchParams({
    utm_source: "web_app",
    utm_medium: "referral",
    utm_campaign: referralCode ? "user_referral" : "direct",
  });
  
  // Add referral code if present
  if (referralCode) {
    utm.append("ref", referralCode);
    utm.append("visitor", visitorId);
  }
  
  // Build final URLs
  const appleUrl = `${appleBaseUrl}?${utm.toString()}`;
  const googleUrl = `${googleBaseUrl}&${utm.toString()}`;
  
  return {
    apple: appleUrl,
    google: googleUrl
  };
}
